import { Controller } from "@hotwired/stimulus"
import Tribute from "tributejs"

// Connects to data-controller="mentions"
// Dual-mode:
//   * Plain <textarea> (chat composer): inserts @[Name](person_id) Markdown tokens.
//   * Trix editor (task form / comments): inserts an ActionText mention attachment
//     (sgid + content) so the server can resolve the mention.
export default class extends Controller {
  static targets = ["field"]
  static values  = { url: String }

  connect() {
    if (!this.hasFieldTarget) return

    const field = this.fieldTarget
    this._isTrix = field.tagName === "TRIX-EDITOR"

    if (this._isTrix) {
      // Trix may not be initialised yet; wait for its editor instance.
      if (field.editor) {
        this._initTrix()
      } else {
        this._onTrixInit = () => this._initTrix()
        field.addEventListener("trix-initialize", this._onTrixInit, { once: true })
      }
    } else {
      this._attach()
    }
  }

  disconnect() {
    if (this._onTrixInit) this.fieldTarget?.removeEventListener("trix-initialize", this._onTrixInit)
    if (this._onReplaced) this.fieldTarget?.removeEventListener("tribute-replaced", this._onReplaced)
    if (this._tribute && this.hasFieldTarget) this._tribute.detach(this.fieldTarget)
  }

  _initTrix() {
    this._editor = this.fieldTarget.editor
    // No selectTemplate — Tribute must not insert plain text; _replaced inserts the
    // ActionText attachment and _pasteHtml removes Tribute's default HTML paste.
    this._buildTribute()
    this._tribute.attach(this.fieldTarget)
    if (this._tribute.range) this._tribute.range.pasteHtml = this._pasteHtml.bind(this)
    this._onReplaced = (e) => this._replaced(e)
    this.fieldTarget.addEventListener("tribute-replaced", this._onReplaced)
  }

  _attach() {
    this._buildTribute(item => `@[${item.original.name}](${item.original.id})`)
    this._tribute.attach(this.fieldTarget)
  }

  _buildTribute(selectTemplate = null) {
    const collection = {
      trigger: "@",
      allowSpaces: true,
      lookup: "name",
      fillAttr: "name",
      menuItemTemplate: (item) =>
        `${item.original.avatar
          ? `<img src="${item.original.avatar}" height="18" width="18" class="rounded-circle me-2" alt="">`
          : ""}${item.string}`,
      values: (text, cb) => this._fetchUsers(text, cb)
    }
    if (selectTemplate) collection.selectTemplate = selectTemplate

    this._tribute = new Tribute({ collection: [collection] })
  }

  // Trix mention: replace the typed token with an ActionText attachment.
  _replaced(e) {
    const mention = e.detail.item?.original
    if (!mention || !this._editor || !mention.sgid) return
    const attachment = new Trix.Attachment({ sgid: mention.sgid, content: mention.content })
    this._editor.insertAttachment(attachment)
  }

  _pasteHtml(html, startPos, endPos) {
    const position = this._editor.getPosition()
    this._editor.setSelectedRange([position - endPos + startPos, position + 1])
    this._editor.deleteInDirection("backward")
  }

  _fetchUsers(text, callback) {
    const url = this.urlValue || "/mentions.json"
    fetch(`${url}?query=${encodeURIComponent(text)}`)
      .then(r => r.json())
      .then(users => callback(users))
      .catch(() => callback([]))
  }
}
